import type { ClipperRankFamily } from '@/lib/ranks/types';

export const STANDING_TOP_LIMIT = 10;

export type StandingLeaderboardEntry = {
  user_id?: string | null;
  name?: string | null;
  avatar_url?: string | null;
  total_views: string;
  video_count: number;
  anonymous?: boolean | null;
  views_tier_id?: string | null;
  rank?: number | null;
  is_viewer?: boolean | null;
  unranked?: boolean | null;
  families?: ClipperRankFamily[] | null;
};

export type StandingLeaderboardItem =
  | { type: 'row'; entry: StandingLeaderboardEntry }
  | { type: 'gap' };

export type StandingLeaderboardPayload = {
  rank: number | null;
  total: number;
  entries: StandingLeaderboardEntry[];
};

function numberedTopRow(entry: StandingLeaderboardEntry, index: number): StandingLeaderboardEntry {
  return {
    ...entry,
    rank: entry.rank ?? index + 1,
    is_viewer: false,
  };
}

export type StandingLeaderboardViewer = {
  user_id?: string | null;
  name?: string | null;
  avatar_url?: string | null;
};

function unrankedViewerRow(
  viewer?: StandingLeaderboardViewer | null,
  viewsTierId?: string | null,
  families?: ClipperRankFamily[] | null,
): StandingLeaderboardEntry {
  return {
    user_id: viewer?.user_id ?? null,
    name: viewer?.name ?? null,
    avatar_url: viewer?.avatar_url ?? null,
    total_views: '0',
    video_count: 0,
    anonymous: false,
    views_tier_id: viewsTierId ?? null,
    rank: null,
    is_viewer: true,
    unranked: true,
    families: families ?? [],
  };
}

export function buildStandingLeaderboardItems({
  topEntries,
  standing,
  viewer,
  viewerViewsTierId,
  topLimit = STANDING_TOP_LIMIT,
}: {
  topEntries: StandingLeaderboardEntry[];
  standing: StandingLeaderboardPayload | null;
  viewer?: StandingLeaderboardViewer | null;
  viewerViewsTierId?: string | null;
  topLimit?: number;
}): StandingLeaderboardItem[] {
  const top = (topEntries || []).slice(0, topLimit).map(numberedTopRow);
  const viewerRank = standing?.rank ?? null;
  const viewerEntry = standing?.entries.find((entry) => entry.is_viewer) ?? null;

  if (viewerRank != null && viewerRank >= 1 && viewerRank <= top.length) {
    const overlay = viewerEntry ?? top[viewerRank - 1];
    top[viewerRank - 1] = {
      ...overlay,
      rank: viewerRank,
      is_viewer: true,
    };
  }

  const items: StandingLeaderboardItem[] = top.map((entry) => ({ type: 'row', entry }));
  if (viewerRank == null) {
    items.push({ type: 'gap' });
    items.push({
      type: 'row',
      entry: unrankedViewerRow(viewer, viewerViewsTierId, viewerEntry?.families),
    });
    return items;
  }
  if (viewerRank <= top.length) return items;

  const extra = (standing?.entries ?? []).filter((entry) => (entry.rank ?? 0) > top.length);
  if (extra.length === 0) {
    if (!viewerEntry) return items;
    if (viewerRank > top.length + 1) items.push({ type: 'gap' });
    items.push({ type: 'row', entry: { ...viewerEntry, rank: viewerRank, is_viewer: true } });
    return items;
  }

  const firstExtraRank = extra[0].rank ?? 0;
  if (firstExtraRank > top.length + 1) items.push({ type: 'gap' });
  for (const entry of extra) {
    items.push({ type: 'row', entry });
  }
  return items;
}
